import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  UserRole,
  Property,
  Resident,
  BookingRequest,
  AttendanceRecord,
  StaffMember,
  StaffTask,
  BroadcastNotification,
  MealPlanDay,
  ChatMessage,
  MaintenanceTicket,
  PaymentReceipt,
  UserAccount,
  Bed,
  BedStatus,
  Lead,
  LeadStage,
  ElectricityMeterReading,
  PropertyElectricityReconciliation,
  SecurityDepositRecord,
  RentAgreement,
  VisitorPass,
  AuditLogEntry,
  SystemSettings,
  RolePermissions,
  PendingCustomerAction,
  Organization,
  Stay,
  RentPlan,
  Invoice,
  Payment,
  PaymentAllocation,
  DepositTransaction,
  Notice,
  Checkout,
  CheckoutSettlement,
  PermissionKey,
} from '../types';
import {
  DEFAULT_ORGANIZATION_ID,
  allocateVerifiedPayment,
  buildAuditEntry,
  buildMonthlyInvoice,
  canBedBeAssigned,
  canonicalBedStatus,
  deriveInitialRentPlans,
  deriveInitialStays,
  hasPermission as hasWorkflowPermission,
  makeCheckout,
  makeNotice,
  makePayment,
  summarizeResidentLedger,
} from '../domain/productionWorkflow';
import {
  isProductionApiEnabled,
  loadProductionSnapshot,
  saveProductionSnapshot,
  clearAuthToken,
} from '../services/productionApi';
import {
  INITIAL_PROPERTIES,
  INITIAL_RESIDENTS,
  INITIAL_BOOKING_REQUESTS,
  INITIAL_ATTENDANCE,
  INITIAL_STAFF,
  INITIAL_TASKS,
  INITIAL_BROADCASTS,
  INITIAL_MEAL_PLAN,
  INITIAL_CHAT,
  INITIAL_TICKETS,
  INITIAL_USERS,
  INITIAL_BEDS,
  INITIAL_LEADS,
  INITIAL_METER_READINGS,
  INITIAL_RECONCILIATION,
  INITIAL_DEPOSITS,
  INITIAL_AGREEMENTS,
  INITIAL_VISITORS,
  INITIAL_AUDIT_LOGS,
  INITIAL_SETTINGS,
  DEFAULT_ROLE_PERMISSIONS,
  INITIAL_STAYS,
  INITIAL_INVOICES,
  INITIAL_CHECKOUT_SETTLEMENTS,
  DEMO_PROPERTIES,
  DEMO_RESIDENTS,
  DEMO_BOOKING_REQUESTS,
} from '../mockData';

interface AppContextType {
  role: UserRole;
  setRole: (role: UserRole) => void;
  setRoleState: (role: UserRole) => void;
  currentUser: UserAccount | null;
  organizations: Organization[];
  users: UserAccount[];
  properties: Property[];
  residents: Resident[];
  bookingRequests: BookingRequest[];
  attendance: AttendanceRecord[];
  staff: StaffMember[];
  currentStaff: StaffMember;
  setCurrentStaffId: (staffId: string) => void;
  tasks: StaffTask[];
  broadcasts: BroadcastNotification[];
  mealPlan: MealPlanDay[];
  chatMessages: ChatMessage[];
  tickets: MaintenanceTicket[];
  currentResident: Resident | null;
  activeProperty: Property;
  selectedPGForDetail: Property | null;
  setSelectedPGForDetail: (property: Property | null) => void;
  
  // Auth Modal State & Controls
  showAuthModal: boolean;
  setShowAuthModal: (show: boolean) => void;
  authModalOpen: boolean;
  setAuthModalOpen: (open: boolean) => void;
  authModalMode: 'login' | 'register';
  setAuthModalMode: (mode: 'login' | 'register') => void;
  authInitialRole?: UserRole;
  setAuthInitialRole?: (role: UserRole) => void;
  openAuthModal: (mode?: 'login' | 'register', targetRole?: UserRole) => void;
  login: (email: string, password?: string, requestedRole?: UserRole, isDemo?: boolean) => { success: boolean; message?: string };
  register: (accountData: {
    name: string;
    email: string;
    phone: string;
    role: UserRole;
    password?: string;
    roomNumber?: string;
    staffRole?: string;
  }) => { success: boolean; message?: string };
  logout: () => void;
  profileModalOpen: boolean;
  setProfileModalOpen: (open: boolean) => void;
  updateUserProfile: (updates: Partial<UserAccount>) => { success: boolean; message?: string };

  // Customer Pending Action & Confirmation
  pendingAction: PendingCustomerAction | null;
  setPendingAction: (action: PendingCustomerAction | null) => void;
  confirmedAction: { referenceId: string; action: PendingCustomerAction } | null;
  setConfirmedAction: (item: { referenceId: string; action: PendingCustomerAction } | null) => void;
  confirmDirectAction: (action: PendingCustomerAction) => { referenceId: string };

  // Enterprise State & Management
  beds: Bed[];
  updateBedStatus: (bedId: string, status: BedStatus, tenantId?: string, tenantName?: string) => void;
  rentPlans: RentPlan[];
  payments: Payment[];
  paymentAllocations: PaymentAllocation[];
  depositTransactions: DepositTransaction[];
  notices: Notice[];
  checkouts: Checkout[];
  checkoutSettlements: CheckoutSettlement[];
  hasPermission: (permission: PermissionKey) => boolean;
  generateMonthlyInvoice: (residentId: string, monthDate?: Date) => Invoice | null;
  recordPayment: (residentId: string, amount: number, method: string, status?: Payment['status']) => Payment;
  verifyPayment: (paymentId: string) => void;
  getResidentLedgerSummary: (residentId: string) => { outstanding: number; advance: number };
  reserveBedForResident: (bedId: string, residentId: string, expiryDate: string) => void;
  confirmMoveIn: (residentId: string, bedId: string, moveInDate?: string, allowExceptionReason?: string) => void;
  transferResidentBed: (residentId: string, newBedId: string, transferDate: string, reason: string, rentChange?: number) => void;
  submitNotice: (residentId: string, requestedCheckoutDate: string, reason: string, comments?: string) => Notice;
  approveNotice: (noticeId: string, approvedCheckoutDate?: string, reason?: string) => void;
  startCheckout: (residentId: string, charges?: Partial<Pick<Checkout, 'electricityCharges' | 'foodCharges' | 'damageCharges' | 'otherDeductions'>>) => Checkout;
  completeCheckout: (checkoutId: string) => void;
  leads: Lead[];
  addLead: (lead: Omit<Lead, 'id' | 'createdAt' | 'lastFollowUp'>) => void;
  updateLeadStage: (leadId: string, stage: LeadStage, notes?: string) => void;
  meterReadings: ElectricityMeterReading[];
  addMeterReading: (reading: Omit<ElectricityMeterReading, 'id'>) => void;
  reconciliations: PropertyElectricityReconciliation[];
  depositRecords: SecurityDepositRecord[];
  addDepositDeduction: (depositId: string, deduction: { category: 'Damage' | 'Unpaid Rent' | 'Electricity' | 'Other'; amount: number; reason: string }) => void;
  settleDepositRefund: (depositId: string, transactionId: string) => void;
  agreements: RentAgreement[];
  signAgreement: (agreementId: string, asRole: 'owner' | 'tenant') => void;
  visitorPasses: VisitorPass[];
  addVisitorPass: (pass: Omit<VisitorPass, 'id' | 'status'>) => void;
  updateVisitorStatus: (passId: string, status: VisitorPass['status']) => void;
  auditLogs: AuditLogEntry[];
  logAuditEvent: (action: string, entity: string, details: string) => void;
  settings: SystemSettings;
  updateSettings: (newSettings: Partial<SystemSettings>) => void;
  rolePermissions: Record<string, RolePermissions>;
  updateRolePermissions: (roleName: string, permissions: RolePermissions) => void;
  
  // Phase 1 & 2: Production Ledger, Invoicing, Stay & Checkout
  generateMonthlyInvoices: (monthYear?: string) => number;
  recordPaymentForInvoice: (invoiceId: string, amount: number, paymentMethod: string, notes?: string) => PaymentReceipt;
  recordAdvancePayment: (residentId: string, amount: number, paymentMethod: string) => void;
  processRoomTransfer: (residentId: string, newRoomNumber: string, newBedNumber: string, newMonthlyRent: number, reason: string) => void;
  initiateNoticePeriod: (residentId: string, noticeDate?: string, checkoutDate?: string, reason?: string) => void;
  executeCheckoutSettlement: (settlement: Omit<CheckoutSettlement, 'id'>) => CheckoutSettlement;
  updateResidentStatus: (residentId: string, status: any) => void;
  updatePropertyModules: (propertyId: string, modules: Partial<Property>) => void;

  // Navigation & Interactive Walkthrough
  globalSearchQuery: string;
  setGlobalSearchQuery: (query: string) => void;
  showAIOnboardingModal: boolean;
  setShowAIOnboardingModal: (show: boolean) => void;
  showVirtualTourModal: boolean;
  setShowVirtualTourModal: (show: boolean) => void;
  virtualTourRoom: string;
  setVirtualTourRoom: (room: string) => void;

  // Actions
  addProperty: (property: Omit<Property, 'id'>) => void;
  addBookingRequest: (request: Omit<BookingRequest, 'id' | 'status' | 'requestDate'>) => void;
  approveBookingRequest: (requestId: string, roomNumber?: string, bedNumber?: string) => void;
  rejectBookingRequest: (requestId: string) => void;
  cancelBookingRequest: (requestId: string) => void;
  rescheduleVisit: (requestId: string, newDate: string, newTimeSlot: string) => void;
  addResident: (resident: Omit<Resident, 'id'>) => void;
  updateRentStatus: (residentId: string, status: 'Paid' | 'Pending' | 'Overdue') => void;
  payRentSimulation: (residentId: string, method: string) => PaymentReceipt;
  recordAttendance: (record: Omit<AttendanceRecord, 'id' | 'date' | 'timestamp'>) => void;
  toggleStaffClockIn: (staffId: string) => void;
  addStaffMember: (newStaff: Omit<StaffMember, 'id'>) => void;
  updateStaffMember: (staffId: string, updates: Partial<StaffMember>) => void;
  deleteStaffMember: (staffId: string) => void;
  toggleTaskCompleted: (taskId: string) => void;
  addTask: (task: Omit<StaffTask, 'id' | 'completed'>) => void;
  addBroadcast: (broadcast: Omit<BroadcastNotification, 'id' | 'timestamp'>) => void;
  updateMealPlanDay: (day: MealPlanDay['day'], field: keyof MealPlanDay, value: string) => void;
  sendChatMessage: (text: string, isOwner: boolean) => void;
  addMaintenanceTicket: (ticket: Omit<MaintenanceTicket, 'id' | 'createdAt' | 'status'>) => void;
  updateTicketStatus: (ticketId: string, status: MaintenanceTicket['status']) => void;
  resetToDemoData: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

const STORAGE_KEYS = {
  ORGANIZATIONS: 'pgwalo_organizations',
  ROLE: 'pgwalo_role',
  CURRENT_USER: 'pgwalo_current_user',
  USERS: 'pgwalo_users',
  CURRENT_STAFF_ID: 'pgwalo_current_staff_id',
  PROPERTIES: 'pgwalo_properties',
  RESIDENTS: 'pgwalo_residents',
  BOOKINGS: 'pgwalo_bookings',
  ATTENDANCE: 'pgwalo_attendance',
  STAFF: 'pgwalo_staff',
  TASKS: 'pgwalo_tasks',
  BROADCASTS: 'pgwalo_broadcasts',
  MEALS: 'pgwalo_meals',
  CHAT: 'pgwalo_chat',
  TICKETS: 'pgwalo_tickets',
  BEDS: 'pgwalo_beds',
  LEADS: 'pgwalo_leads',
  METER_READINGS: 'pgwalo_meter_readings',
  RECONCILIATION: 'pgwalo_reconciliation',
  DEPOSITS: 'pgwalo_deposits',
  AGREEMENTS: 'pgwalo_agreements',
  VISITORS: 'pgwalo_visitors',
  AUDIT_LOGS: 'pgwalo_audit_logs',
  SETTINGS: 'pgwalo_settings',
  PERMISSIONS: 'pgwalo_permissions',
  CHECKOUT_SETTLEMENTS: 'pgwalo_checkout_settlements',
  STAYS: 'pgwalo_stays',
  RENT_PLANS: 'pgwalo_rent_plans',
  INVOICES: 'pgwalo_invoices',
  PAYMENTS: 'pgwalo_payments',
  PAYMENT_ALLOCATIONS: 'pgwalo_payment_allocations',
  DEPOSIT_TRANSACTIONS: 'pgwalo_deposit_transactions',
  NOTICES: 'pgwalo_notices',
  CHECKOUTS: 'pgwalo_checkouts',
};

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [role, setRoleState] = useState<UserRole>(() => {
    const savedUser = localStorage.getItem(STORAGE_KEYS.CURRENT_USER);
    if (!savedUser) return 'public';
    try {
      const parsed: UserAccount = JSON.parse(savedUser);
      if (
        !parsed ||
        parsed.id.startsWith('user-owner') ||
        parsed.id.startsWith('user-resident') ||
        parsed.id.startsWith('user-staff') ||
        parsed.id.startsWith('user-admin') ||
        parsed.id.startsWith('user-manager') ||
        parsed.id.startsWith('user-warden') ||
        parsed.id.startsWith('user-accountant')
      ) {
        return 'public';
      }
      return parsed.role || 'public';
    } catch {
      return 'public';
    }
  });

  const [organizations, setOrganizations] = useState<Organization[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.ORGANIZATIONS);
    return saved
      ? JSON.parse(saved)
      : [
          {
            id: DEFAULT_ORGANIZATION_ID,
            name: 'PGWalo Operations',
            ownerUserId: 'user-owner',
            accountState: 'Trial / Pending Setup',
            subscriptionPlan: 'Trial',
            createdAt: new Date().toISOString(),
          },
        ];
  });

  const [properties, setProperties] = useState<Property[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.PROPERTIES);
    const savedUser = localStorage.getItem(STORAGE_KEYS.CURRENT_USER);
    let isDemoUser = false;

    if (savedUser) {
      try {
        const user: UserAccount = JSON.parse(savedUser);
        isDemoUser = user.isDemo || false;
      } catch {
        isDemoUser = false;
      }
    }

    const source: Property[] = saved ? JSON.parse(saved) : INITIAL_PROPERTIES;

    // Filter out demo data for non-demo users
    const filteredSource = isDemoUser ? source : source.filter(p => !p.id.startsWith('demo-'));

    return filteredSource.map((property) => ({
      ...property,
      organizationId: property.organizationId || DEFAULT_ORGANIZATION_ID,
      status: property.status || 'Active',
      defaultRentDueDay: property.defaultRentDueDay || 7,
      modules: property.modules || {
        foodManagement: property.foodIncluded,
        attendance: true,
        visitorManagement: true,
        onlinePayments: true,
        chat: true,
      },
    }));
  });

  const [residents, setResidents] = useState<Resident[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.RESIDENTS);
    const savedUser = localStorage.getItem(STORAGE_KEYS.CURRENT_USER);
    let isDemoUser = false;

    if (savedUser) {
      try {
        const user: UserAccount = JSON.parse(savedUser);
        isDemoUser = user.isDemo || false;
      } catch {
        isDemoUser = false;
      }
    }

    const source: Resident[] = saved ? JSON.parse(saved) : INITIAL_RESIDENTS;
    const filteredSource = isDemoUser ? source : source.filter(r => !r.id.startsWith('demo-'));

    return filteredSource.map((resident) => ({
      ...resident,
      organizationId: resident.organizationId || DEFAULT_ORGANIZATION_ID,
      status: resident.status || 'Active',
      depositState: resident.depositState || 'Held',
      agreementState: resident.agreementState || (resident.kycVerified ? 'Active' : 'Pending'),
      previousDues: resident.previousDues || 0,
      advanceBalance: resident.advanceBalance || 0,
      outstandingBalance: resident.outstandingBalance || (resident.rentStatus === 'Paid' ? 0 : resident.monthlyRent),
      noticePeriodDays: resident.noticePeriodDays || 30,
    }));
  });

  const [bookingRequests, setBookingRequests] = useState<BookingRequest[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.BOOKINGS);
    const savedUser = localStorage.getItem(STORAGE_KEYS.CURRENT_USER);
    let isDemoUser = false;

    if (savedUser) {
      try {
        const user: UserAccount = JSON.parse(savedUser);
        isDemoUser = user.isDemo || false;
      } catch {
        isDemoUser = false;
      }
    }

    const source: BookingRequest[] = saved ? JSON.parse(saved) : INITIAL_BOOKING_REQUESTS;
    return isDemoUser ? source : source.filter(b => !b.id.startsWith('demo-'));
  });

  const [attendance, setAttendance] = useState<AttendanceRecord[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.ATTENDANCE);
    return saved ? JSON.parse(saved) : INITIAL_ATTENDANCE;
  });

  const [staff, setStaff] = useState<StaffMember[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.STAFF);
    return saved ? JSON.parse(saved) : INITIAL_STAFF;
  });

  const [tasks, setTasks] = useState<StaffTask[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.TASKS);
    return saved ? JSON.parse(saved) : INITIAL_TASKS;
  });

  const [broadcasts, setBroadcasts] = useState<BroadcastNotification[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.BROADCASTS);
    return saved ? JSON.parse(saved) : INITIAL_BROADCASTS;
  });

  const [mealPlan, setMealPlan] = useState<MealPlanDay[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.MEALS);
    return saved ? JSON.parse(saved) : INITIAL_MEAL_PLAN;
  });

  const [chatMessages, setChatMessages] = useState<ChatMessage[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.CHAT);
    return saved ? JSON.parse(saved) : INITIAL_CHAT;
  });

  const [tickets, setTickets] = useState<MaintenanceTicket[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.TICKETS);
    return saved ? JSON.parse(saved) : INITIAL_TICKETS;
  });

  const [users, setUsers] = useState<UserAccount[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.USERS);
    if (saved) {
      try {
        const parsed: UserAccount[] = JSON.parse(saved);
        return parsed.filter(
          (u) =>
            !u.id.startsWith('user-owner') &&
            !u.id.startsWith('user-resident') &&
            !u.id.startsWith('user-staff') &&
            !u.id.startsWith('user-admin') &&
            !u.id.startsWith('user-manager') &&
            !u.id.startsWith('user-warden') &&
            !u.id.startsWith('user-accountant')
        );
      } catch {
        return [];
      }
    }
    return [];
  });

  const [currentUser, setCurrentUser] = useState<UserAccount | null>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.CURRENT_USER);
    if (saved) {
      try {
        const parsed: UserAccount = JSON.parse(saved);
        if (
          !parsed ||
          parsed.id.startsWith('user-owner') ||
          parsed.id.startsWith('user-resident') ||
          parsed.id.startsWith('user-staff') ||
          parsed.id.startsWith('user-admin') ||
          parsed.id.startsWith('user-manager') ||
          parsed.id.startsWith('user-warden') ||
          parsed.id.startsWith('user-accountant')
        ) {
          localStorage.removeItem(STORAGE_KEYS.CURRENT_USER);
          localStorage.removeItem(STORAGE_KEYS.ROLE);
          return null;
        }
        return parsed;
      } catch {
        return null;
      }
    }
    return null;
  });

  const [currentStaffId, setCurrentStaffId] = useState<string>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.CURRENT_STAFF_ID);
    return saved || 'staff-1';
  });

  // Enterprise States
  const [beds, setBeds] = useState<Bed[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.BEDS);
    return saved ? JSON.parse(saved) : INITIAL_BEDS;
  });

  const [leads, setLeads] = useState<Lead[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.LEADS);
    return saved ? JSON.parse(saved) : INITIAL_LEADS;
  });

  const [meterReadings, setMeterReadings] = useState<ElectricityMeterReading[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.METER_READINGS);
    return saved ? JSON.parse(saved) : INITIAL_METER_READINGS;
  });

  const [reconciliations, setReconciliations] = useState<PropertyElectricityReconciliation[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.RECONCILIATION);
    return saved ? JSON.parse(saved) : INITIAL_RECONCILIATION;
  });

  const [depositRecords, setDepositRecords] = useState<SecurityDepositRecord[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.DEPOSITS);
    return saved ? JSON.parse(saved) : INITIAL_DEPOSITS;
  });

  const [agreements, setAgreements] = useState<RentAgreement[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.AGREEMENTS);
    return saved ? JSON.parse(saved) : INITIAL_AGREEMENTS;
  });

  const [visitorPasses, setVisitorPasses] = useState<VisitorPass[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.VISITORS);
    return saved ? JSON.parse(saved) : INITIAL_VISITORS;
  });

  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.AUDIT_LOGS);
    return saved ? JSON.parse(saved) : INITIAL_AUDIT_LOGS;
  });

  const [settings, setSettings] = useState<SystemSettings>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.SETTINGS);
    return saved ? JSON.parse(saved) : INITIAL_SETTINGS;
  });

  const [rolePermissions, setRolePermissions] = useState<Record<string, RolePermissions>>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.PERMISSIONS);
    return saved ? JSON.parse(saved) : DEFAULT_ROLE_PERMISSIONS;
  });

  const [stays, setStays] = useState<Stay[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.STAYS);
    return saved ? JSON.parse(saved) : deriveInitialStays(residents, beds);
  });

  const [invoices, setInvoices] = useState<Invoice[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.INVOICES);
    if (saved) return JSON.parse(saved);
    const now = new Date();
    return residents
      .map((resident) => {
        const plan = deriveInitialRentPlans([resident], properties)[0];
        const invoice = buildMonthlyInvoice(resident, plan, now, []);
        if (!invoice) return null;
        if (resident.rentStatus === 'Paid') {
          return { ...invoice, verifiedPaidAmount: invoice.amount, status: 'Paid' as const };
        }
        return invoice;
      })
      .filter(Boolean) as Invoice[];
  });

  const [rentPlans, setRentPlans] = useState<RentPlan[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.RENT_PLANS);
    return saved ? JSON.parse(saved) : deriveInitialRentPlans(residents, properties);
  });

  const [payments, setPayments] = useState<Payment[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.PAYMENTS);
    return saved ? JSON.parse(saved) : [];
  });

  const [paymentAllocations, setPaymentAllocations] = useState<PaymentAllocation[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.PAYMENT_ALLOCATIONS);
    return saved ? JSON.parse(saved) : [];
  });

  const [depositTransactions, setDepositTransactions] = useState<DepositTransaction[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.DEPOSIT_TRANSACTIONS);
    if (saved) return JSON.parse(saved);
    return residents.map((resident) => ({
      id: `dep-txn-${resident.id}`,
      organizationId: resident.organizationId || DEFAULT_ORGANIZATION_ID,
      residentId: resident.id,
      propertyId: resident.propertyId,
      type: 'Deposit Received' as const,
      amount: resident.depositAmount,
      reason: 'Opening security deposit balance',
      createdBy: 'system',
      createdAt: resident.moveInDate,
    }));
  });

  const [notices, setNotices] = useState<Notice[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.NOTICES);
    return saved ? JSON.parse(saved) : [];
  });

  const [checkouts, setCheckouts] = useState<Checkout[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.CHECKOUTS);
    return saved ? JSON.parse(saved) : [];
  });

  const [checkoutSettlements, setCheckoutSettlements] = useState<CheckoutSettlement[]>(() => {
    const saved = localStorage.getItem('pgwalo_checkout_settlements');
    return saved ? JSON.parse(saved) : [];
  });

  const [globalSearchQuery, setGlobalSearchQuery] = useState('');
  const [showAIOnboardingModal, setShowAIOnboardingModal] = useState(false);
  const [showVirtualTourModal, setShowVirtualTourModal] = useState(false);
  const [virtualTourRoom, setVirtualTourRoom] = useState('Reception & Lobby');

  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<'login' | 'register'>('login');
  const [authInitialRole, setAuthInitialRole] = useState<UserRole>('resident');
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingCustomerAction | null>(null);
  const [confirmedAction, setConfirmedAction] = useState<{ referenceId: string; action: PendingCustomerAction } | null>(null);
  const [selectedPGForDetail, setSelectedPGForDetail] = useState<Property | null>(null);
  const [productionHydrated, setProductionHydrated] = useState(!isProductionApiEnabled());

  // Active entities
  const activeProperty = properties[0] || INITIAL_PROPERTIES[0];
  const currentResident: Resident | null = currentUser?.role === 'resident'
    ? (residents.find(
        (r) =>
          (currentUser.email && r.email.toLowerCase() === currentUser.email.toLowerCase()) ||
          r.id === currentUser.id ||
          r.id === `res-${currentUser.id}`
      ) || null)
    : (currentUser ? null : (residents[0] || null));

  const currentStaff: StaffMember = 
    staff.find((s) => s.id === currentStaffId) ||
    (currentUser?.role === 'staff' ? staff.find((s) => s.name.toLowerCase().includes(currentUser.name.toLowerCase().split(' ')[0])) : null) ||
    staff[0] ||
    INITIAL_STAFF[0];

  useEffect(() => {
    let cancelled = false;
    if (!isProductionApiEnabled()) return;
    loadProductionSnapshot(currentUser?.role || role, currentUser?.organizationId || DEFAULT_ORGANIZATION_ID)
      .then((snapshot) => {
        if (cancelled || !snapshot) return;
        if (snapshot.organizations?.length) setOrganizations(snapshot.organizations);
        if (snapshot.properties?.length) setProperties(snapshot.properties);
        if (snapshot.residents?.length) setResidents(snapshot.residents);
        if (snapshot.beds?.length) setBeds(snapshot.beds);
        if (snapshot.stays?.length) setStays(snapshot.stays);
        if (snapshot.rentPlans?.length) setRentPlans(snapshot.rentPlans);
        if (snapshot.invoices?.length) setInvoices(snapshot.invoices);
        if (snapshot.payments?.length) setPayments(snapshot.payments);
        if (snapshot.paymentAllocations?.length) setPaymentAllocations(snapshot.paymentAllocations);
        if (snapshot.depositTransactions?.length) setDepositTransactions(snapshot.depositTransactions);
        if (snapshot.notices?.length) setNotices(snapshot.notices);
        if (snapshot.checkouts?.length) setCheckouts(snapshot.checkouts);
        if (snapshot.checkoutSettlements?.length) setCheckoutSettlements(snapshot.checkoutSettlements);
        if (snapshot.auditLogs?.length) setAuditLogs(snapshot.auditLogs);
      })
      .catch((error) => {
        console.warn('PGNest production API bootstrap failed; using local cache.', error);
      })
      .finally(() => {
        if (!cancelled) setProductionHydrated(true);
      });
    return () => {
      cancelled = true;
    };
  }, [currentUser?.id, role]);

  // Sync to LocalStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.ROLE, role);
  }, [role]);

  useEffect(() => {
    if (currentUser) {
      localStorage.setItem(STORAGE_KEYS.CURRENT_USER, JSON.stringify(currentUser));
    } else {
      localStorage.removeItem(STORAGE_KEYS.CURRENT_USER);
    }
  }, [currentUser]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.USERS, JSON.stringify(users));
  }, [users]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.ORGANIZATIONS, JSON.stringify(organizations));
  }, [organizations]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.CURRENT_STAFF_ID, currentStaffId);
  }, [currentStaffId]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.PROPERTIES, JSON.stringify(properties));
  }, [properties]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.RESIDENTS, JSON.stringify(residents));
  }, [residents]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.BOOKINGS, JSON.stringify(bookingRequests));
  }, [bookingRequests]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.ATTENDANCE, JSON.stringify(attendance));
  }, [attendance]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.STAFF, JSON.stringify(staff));
  }, [staff]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.TASKS, JSON.stringify(tasks));
  }, [tasks]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.BROADCASTS, JSON.stringify(broadcasts));
  }, [broadcasts]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.MEALS, JSON.stringify(mealPlan));
  }, [mealPlan]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.CHAT, JSON.stringify(chatMessages));
  }, [chatMessages]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.TICKETS, JSON.stringify(tickets));
  }, [tickets]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.BEDS, JSON.stringify(beds));
  }, [beds]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.LEADS, JSON.stringify(leads));
  }, [leads]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.METER_READINGS, JSON.stringify(meterReadings));
  }, [meterReadings]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.RECONCILIATION, JSON.stringify(reconciliations));
  }, [reconciliations]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.DEPOSITS, JSON.stringify(depositRecords));
  }, [depositRecords]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.AGREEMENTS, JSON.stringify(agreements));
  }, [agreements]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.VISITORS, JSON.stringify(visitorPasses));
  }, [visitorPasses]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.AUDIT_LOGS, JSON.stringify(auditLogs));
  }, [auditLogs]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(settings));
  }, [settings]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.PERMISSIONS, JSON.stringify(rolePermissions));
  }, [rolePermissions]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.STAYS, JSON.stringify(stays));
  }, [stays]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.RENT_PLANS, JSON.stringify(rentPlans));
  }, [rentPlans]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.INVOICES, JSON.stringify(invoices));
  }, [invoices]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.PAYMENTS, JSON.stringify(payments));
  }, [payments]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.PAYMENT_ALLOCATIONS, JSON.stringify(paymentAllocations));
  }, [paymentAllocations]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.DEPOSIT_TRANSACTIONS, JSON.stringify(depositTransactions));
  }, [depositTransactions]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.NOTICES, JSON.stringify(notices));
  }, [notices]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.CHECKOUTS, JSON.stringify(checkouts));
  }, [checkouts]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.CHECKOUT_SETTLEMENTS, JSON.stringify(checkoutSettlements));
  }, [checkoutSettlements]);

  useEffect(() => {
    if (!productionHydrated || !isProductionApiEnabled() || !['owner', 'admin'].includes(currentUser?.role || role)) {
      return;
    }
    const timeout = window.setTimeout(() => {
      saveProductionSnapshot(
        currentUser?.role || role,
        {
          organizations,
          properties,
          residents,
          beds,
          stays,
          rentPlans,
          invoices,
          payments,
          paymentAllocations,
          depositTransactions,
          notices,
          checkouts,
          checkoutSettlements,
        },
        currentUser?.organizationId || DEFAULT_ORGANIZATION_ID
      ).catch((error) => {
        console.warn('PGNest production API sync failed; local cache retained.', error);
      });
    }, 800);
    return () => window.clearTimeout(timeout);
  }, [
    productionHydrated,
    currentUser?.role,
    currentUser?.organizationId,
    role,
    organizations,
    properties,
    residents,
    beds,
    stays,
    rentPlans,
    invoices,
    payments,
    paymentAllocations,
    depositTransactions,
    notices,
    checkouts,
    checkoutSettlements,
  ]);

  const hasPermission = (permission: PermissionKey) =>
    hasWorkflowPermission(currentUser?.role || role, rolePermissions, permission);

  const addAuditEntry = (params: Parameters<typeof buildAuditEntry>[0]) => {
    setAuditLogs((prev) => [buildAuditEntry(params), ...prev.slice(0, 199)]);
  };

  const updateResidentLedgerStatus = (residentId: string, nextInvoices = invoices, nextAllocations = paymentAllocations) => {
    const summary = summarizeResidentLedger(residentId, nextInvoices, nextAllocations);
    setResidents((prev) =>
      prev.map((resident) => {
        if (resident.id !== residentId) return resident;
        return {
          ...resident,
          outstandingBalance: summary.outstanding,
          advanceBalance: summary.advance,
          rentStatus: summary.outstanding === 0 ? 'Paid' : resident.rentStatus === 'Overdue' ? 'Overdue' : 'Pending',
          lastPaymentDate: summary.outstanding === 0 ? new Date().toISOString().split('T')[0] : resident.lastPaymentDate,
        };
      })
    );
  };

  const updateBedStatus = (bedId: string, status: BedStatus, tenantId?: string, tenantName?: string) => {
    const existingBed = beds.find((b) => b.id === bedId);
    if (!existingBed) return;
    if (!hasPermission('room.assign')) {
      logAuditEvent('Permission Denied', `Bed ${bedId}`, `Attempted bed status update to ${status}`);
      return;
    }
    if (canonicalBedStatus(status) === 'Occupied') {
      const overlapping = beds.some(
        (bed) =>
          bed.id === bedId &&
          canonicalBedStatus(bed.status) === 'Occupied' &&
          bed.currentTenantId &&
          tenantId &&
          bed.currentTenantId !== tenantId
      );
      if (overlapping) {
        logAuditEvent('Blocked Double Allocation', `Bed ${bedId}`, 'Cannot assign occupied bed to another active resident');
        return;
      }
    }

    setBeds((prev) =>
      prev.map((b) =>
        b.id === bedId
          ? {
              ...b,
              status,
              currentTenantId: tenantId ?? (canonicalBedStatus(status) === 'Vacant' ? undefined : b.currentTenantId),
              currentTenantName: tenantName ?? (canonicalBedStatus(status) === 'Vacant' ? undefined : b.currentTenantName),
              reservedForResidentId: canonicalBedStatus(status) === 'Reserved' ? tenantId : b.reservedForResidentId,
            }
          : b
      )
    );
    addAuditEntry({
      user: currentUser,
      role,
      action: 'Bed Status Changed',
      entityType: 'Bed',
      entityId: bedId,
      previousValue: existingBed,
      newValue: { status, tenantId, tenantName },
      propertyId: existingBed.propertyId,
    });
  };

  const getResidentLedgerSummary = (residentId: string) =>
    summarizeResidentLedger(residentId, invoices, paymentAllocations);

  const generateMonthlyInvoice = (residentId: string, monthDate = new Date()) => {
    if (!hasPermission('invoice.create')) return null;
    const resident = residents.find((item) => item.id === residentId);
    const rentPlan = rentPlans.find(
      (item) => item.residentId === residentId && item.status === 'Active'
    );
    if (!resident || !rentPlan) return null;
    const invoice = buildMonthlyInvoice(resident, rentPlan, monthDate, invoices);
    if (!invoice) return null;
    setInvoices((prev) => [invoice, ...prev]);
    updateResidentLedgerStatus(residentId, [invoice, ...invoices], paymentAllocations);
    addAuditEntry({
      user: currentUser,
      role,
      action: 'Invoice Created',
      entityType: 'Invoice',
      entityId: invoice.id,
      newValue: invoice,
      propertyId: invoice.propertyId,
    });
    return invoice;
  };

  const recordPayment = (
    residentId: string,
    amount: number,
    method: string,
    status: Payment['status'] = 'Pending Verification'
  ) => {
    if (!hasPermission('payment.record') && currentUser?.role !== 'resident' && role !== 'resident') {
      throw new Error('You do not have permission to record payments.');
    }
    const resident = residents.find((item) => item.id === residentId) || currentResident;
    if (!resident || resident.id !== residentId) throw new Error('Resident not found.');
    const payment = makePayment({ resident, amount, method, status });
    setPayments((prev) => [payment, ...prev]);
    addAuditEntry({
      user: currentUser,
      role,
      action: 'Payment Created',
      entityType: 'Payment',
      entityId: payment.id,
      newValue: payment,
      propertyId: payment.propertyId,
      reason: status === 'Pending Verification' ? 'Resident/manual payment submitted for verification' : undefined,
    });
    if (status === 'Verified') {
      const allocated = allocateVerifiedPayment(payment, invoices, paymentAllocations);
      setInvoices(allocated.invoices);
      setPaymentAllocations(allocated.allocations);
      updateResidentLedgerStatus(residentId, allocated.invoices, allocated.allocations);
    }
    return payment;
  };

  const verifyPayment = (paymentId: string) => {
    if (!hasPermission('payment.verify')) {
      throw new Error('You do not have permission to verify payments.');
    }
    const target = payments.find((payment) => payment.id === paymentId);
    if (!target || target.status === 'Verified') return;
    const verifiedPayment: Payment = {
      ...target,
      status: 'Verified',
      verifiedAt: new Date().toISOString(),
    };
    const nextPayments = payments.map((payment) => (payment.id === paymentId ? verifiedPayment : payment));
    const allocated = allocateVerifiedPayment(verifiedPayment, invoices, paymentAllocations);
    setPayments(nextPayments);
    setInvoices(allocated.invoices);
    setPaymentAllocations(allocated.allocations);
    updateResidentLedgerStatus(verifiedPayment.residentId, allocated.invoices, allocated.allocations);
    addAuditEntry({
      user: currentUser,
      role,
      action: 'Payment Verified',
      entityType: 'Payment',
      entityId: paymentId,
      previousValue: target,
      newValue: verifiedPayment,
      propertyId: verifiedPayment.propertyId,
    });
  };

  const reserveBedForResident = (bedId: string, residentId: string, expiryDate: string) => {
    if (!hasPermission('room.assign')) throw new Error('You do not have permission to reserve beds.');
    const bed = beds.find((item) => item.id === bedId);
    const resident = residents.find((item) => item.id === residentId);
    if (!bed || !resident) throw new Error('Bed or resident not found.');
    if (!canBedBeAssigned(bed, residentId)) throw new Error('This bed is not available for reservation.');
    setBeds((prev) =>
      prev.map((item) =>
        item.id === bedId
          ? {
              ...item,
              status: 'Reserved',
              reservedForResidentId: residentId,
              currentTenantId: residentId,
              currentTenantName: resident.name,
              reservationExpiry: expiryDate,
            }
          : item
      )
    );
    setResidents((prev) =>
      prev.map((item) => (item.id === residentId ? { ...item, status: 'Reserved' } : item))
    );
    addAuditEntry({
      user: currentUser,
      role,
      action: 'Bed Reserved',
      entityType: 'Bed',
      entityId: bedId,
      previousValue: bed,
      newValue: { residentId, expiryDate },
      propertyId: bed.propertyId,
    });
  };

  const confirmMoveIn = (
    residentId: string,
    bedId: string,
    moveInDate = new Date().toISOString().split('T')[0],
    allowExceptionReason?: string
  ) => {
    if (!hasPermission('room.assign')) throw new Error('You do not have permission to confirm move-ins.');
    const resident = residents.find((item) => item.id === residentId);
    const bed = beds.find((item) => item.id === bedId);
    if (!resident || !bed) throw new Error('Resident or bed not found.');
    const missingRequired =
      !resident.monthlyRent ||
      !moveInDate ||
      resident.depositState === 'Unknown' ||
      resident.agreementState === 'Unknown' ||
      !resident.kycVerified;
    if (missingRequired && !allowExceptionReason) {
      throw new Error('Move-in requires rent, move-in date, KYC, deposit state, and agreement state.');
    }
    if (!canBedBeAssigned(bed, residentId)) {
      throw new Error('Cannot confirm move-in because the bed is occupied or reserved for someone else.');
    }

    const stay: Stay = {
      id: `stay-${residentId}-${Date.now()}`,
      organizationId: resident.organizationId || DEFAULT_ORGANIZATION_ID,
      residentId,
      propertyId: resident.propertyId,
      roomId: bed.roomId,
      roomNumber: bed.roomNumber,
      bedId,
      bedNumber: bed.bedNumber,
      startDate: moveInDate,
      monthlyRentAtStart: resident.monthlyRent,
      status: 'Current',
    };
    setStays((prev) => [stay, ...prev]);
    setBeds((prev) =>
      prev.map((item) =>
        item.id === bedId
          ? { ...item, status: 'Occupied', currentTenantId: residentId, currentTenantName: resident.name }
          : item
      )
    );
    setResidents((prev) =>
      prev.map((item) =>
        item.id === residentId
          ? {
              ...item,
              status: 'Active',
              roomNumber: bed.roomNumber,
              bedNumber: bed.bedNumber,
              currentStayId: stay.id,
              moveInDate,
            }
          : item
      )
    );
    addAuditEntry({
      user: currentUser,
      role,
      action: 'Move-In Confirmed',
      entityType: 'Resident',
      entityId: residentId,
      previousValue: resident,
      newValue: { bedId, stay, status: 'Active' },
      propertyId: resident.propertyId,
      reason: allowExceptionReason,
    });
  };

  const transferResidentBed = (
    residentId: string,
    newBedId: string,
    transferDate: string,
    reason: string,
    rentChange?: number
  ) => {
    if (!hasPermission('room.transfer')) throw new Error('You do not have permission to transfer beds.');
    if (!reason.trim()) throw new Error('Room transfer requires a reason.');
    const resident = residents.find((item) => item.id === residentId);
    const newBed = beds.find((item) => item.id === newBedId);
    if (!resident || resident.status !== 'Active' || !newBed) throw new Error('Only active residents can be transferred.');
    if (!canBedBeAssigned(newBed, residentId)) throw new Error('Target bed is not available.');
    const currentStay = stays.find((stay) => stay.residentId === residentId && stay.status === 'Current');
    const nextRent = rentChange || resident.monthlyRent;
    const newStay: Stay = {
      id: `stay-${residentId}-${Date.now()}`,
      organizationId: resident.organizationId || DEFAULT_ORGANIZATION_ID,
      residentId,
      propertyId: resident.propertyId,
      roomId: newBed.roomId,
      roomNumber: newBed.roomNumber,
      bedId: newBed.id,
      bedNumber: newBed.bedNumber,
      startDate: transferDate,
      monthlyRentAtStart: nextRent,
      transferReason: reason,
      status: 'Current',
    };
    setStays((prev) => [
      newStay,
      ...prev.map((stay) =>
        stay.id === currentStay?.id ? { ...stay, status: 'Closed' as const, endDate: transferDate, transferReason: reason } : stay
      ),
    ]);
    setBeds((prev) =>
      prev.map((bed) => {
        if (bed.id === currentStay?.bedId) {
          return { ...bed, status: 'Vacant', currentTenantId: undefined, currentTenantName: undefined };
        }
        if (bed.id === newBedId) {
          return { ...bed, status: 'Occupied', currentTenantId: residentId, currentTenantName: resident.name };
        }
        return bed;
      })
    );
    setResidents((prev) =>
      prev.map((item) =>
        item.id === residentId
          ? {
              ...item,
              roomNumber: newBed.roomNumber,
              bedNumber: newBed.bedNumber,
              monthlyRent: nextRent,
              currentStayId: newStay.id,
            }
          : item
      )
    );
    if (rentChange && rentChange !== resident.monthlyRent) {
      setRentPlans((prev) => [
        {
          id: `rent-plan-${residentId}-${Date.now()}`,
          organizationId: resident.organizationId || DEFAULT_ORGANIZATION_ID,
          residentId,
          propertyId: resident.propertyId,
          monthlyRent: rentChange,
          dueDay: settings.rentDueDay || 7,
          effectiveFrom: transferDate,
          status: 'Active',
          revisionReason: reason,
        },
        ...prev.map((plan) =>
          plan.residentId === residentId && plan.status === 'Active'
            ? { ...plan, status: 'Ended' as const, effectiveTo: transferDate }
            : plan
        ),
      ]);
    }
    addAuditEntry({
      user: currentUser,
      role,
      action: 'Room Transfer',
      entityType: 'Resident',
      entityId: residentId,
      previousValue: { resident, currentStay },
      newValue: { newStay, rentChange },
      propertyId: resident.propertyId,
      reason,
    });
  };

  const submitNotice = (residentId: string, requestedCheckoutDate: string, reason: string, comments?: string) => {
    const resident = residents.find((item) => item.id === residentId);
    if (!resident || resident.status !== 'Active') throw new Error('Only active residents can submit notice.');
    const notice = makeNotice({ resident, requestedCheckoutDate, reason, comments });
    setNotices((prev) => [notice, ...prev]);
    setResidents((prev) => prev.map((item) => (item.id === residentId ? { ...item, status: 'Notice Period' } : item)));
    setBeds((prev) =>
      prev.map((bed) =>
        bed.currentTenantId === residentId
          ? { ...bed, status: 'Notice Period', nextAvailableDate: notice.approvedCheckoutDate }
          : bed
      )
    );
    addAuditEntry({
      user: currentUser,
      role,
      action: 'Notice Submitted',
      entityType: 'Notice',
      entityId: notice.id,
      newValue: notice,
      propertyId: resident.propertyId,
      reason,
    });
    return notice;
  };

  const approveNotice = (noticeId: string, approvedCheckoutDate?: string, reason?: string) => {
    const notice = notices.find((item) => item.id === noticeId);
    if (!notice) return;
    const nextNotice = {
      ...notice,
      status: approvedCheckoutDate && approvedCheckoutDate !== notice.approvedCheckoutDate ? 'Modified' as const : 'Approved' as const,
      approvedCheckoutDate: approvedCheckoutDate || notice.approvedCheckoutDate,
      ownerReason: reason,
    };
    setNotices((prev) => prev.map((item) => (item.id === noticeId ? nextNotice : item)));
    addAuditEntry({
      user: currentUser,
      role,
      action: 'Notice Approved',
      entityType: 'Notice',
      entityId: noticeId,
      previousValue: notice,
      newValue: nextNotice,
      propertyId: notice.propertyId,
      reason,
    });
  };

  const startCheckout = (
    residentId: string,
    charges: Partial<Pick<Checkout, 'electricityCharges' | 'foodCharges' | 'damageCharges' | 'otherDeductions'>> = {}
  ) => {
    if (!hasPermission('resident.checkout')) throw new Error('You do not have permission to start checkout.');
    const resident = residents.find((item) => item.id === residentId);
    const activeStay = stays.find((stay) => stay.residentId === residentId && stay.status === 'Current');
    if (!resident || !activeStay) throw new Error('Resident stay not found for checkout.');
    if (!['Active', 'Notice Period', 'Checkout Pending'].includes(resident.status || 'Active')) {
      throw new Error('Checkout is only available for active, notice period, or checkout pending residents.');
    }
    const checkout = makeCheckout({
      resident,
      bedId: activeStay.bedId,
      invoices,
      depositTransactions,
      electricityCharges: charges.electricityCharges,
      foodCharges: charges.foodCharges,
      damageCharges: charges.damageCharges,
      otherDeductions: charges.otherDeductions,
    });
    setCheckouts((prev) => [checkout, ...prev]);
    setResidents((prev) =>
      prev.map((item) => (item.id === residentId ? { ...item, status: 'Checkout Pending', checkoutId: checkout.id } : item))
    );
    addAuditEntry({
      user: currentUser,
      role,
      action: 'Checkout Started',
      entityType: 'Checkout',
      entityId: checkout.id,
      newValue: checkout,
      propertyId: resident.propertyId,
    });
    return checkout;
  };

  const completeCheckout = (checkoutId: string) => {
    if (!hasPermission('resident.checkout')) throw new Error('You do not have permission to complete checkout.');
    const checkout = checkouts.find((item) => item.id === checkoutId);
    if (!checkout) return;
    const resident = residents.find((item) => item.id === checkout.residentId);
    if (!resident) return;
    const completed: Checkout = { ...checkout, status: 'Completed', completedAt: new Date().toISOString() };
    setCheckouts((prev) => prev.map((item) => (item.id === checkoutId ? completed : item)));
    setResidents((prev) => prev.map((item) => (item.id === resident.id ? { ...item, status: 'Checked Out' } : item)));
    setStays((prev) =>
      prev.map((stay) =>
        stay.residentId === resident.id && stay.status === 'Current'
          ? { ...stay, status: 'Closed', endDate: new Date().toISOString().split('T')[0] }
          : stay
      )
    );
    setBeds((prev) =>
      prev.map((bed) =>
        bed.id === checkout.bedId
          ? {
              ...bed,
              status: 'Vacant',
              currentTenantId: undefined,
              currentTenantName: undefined,
              reservedForResidentId: undefined,
            }
          : bed
      )
    );
    const deductionTotal =
      checkout.rentPending +
      checkout.electricityCharges +
      checkout.foodCharges +
      checkout.damageCharges +
      checkout.otherDeductions;
    if (deductionTotal > 0) {
      setDepositTransactions((prev) => [
        {
          id: `dep-ded-${checkout.id}`,
          organizationId: checkout.organizationId,
          residentId: checkout.residentId,
          propertyId: checkout.propertyId,
          type: 'Deposit Deduction',
          amount: deductionTotal,
          reason: 'Checkout settlement deductions',
          createdBy: currentUser?.id || 'system',
          createdAt: new Date().toISOString(),
        },
        ...prev,
      ]);
    }
    if (checkout.refundAmount > 0) {
      setDepositTransactions((prev) => [
        {
          id: `dep-ref-${checkout.id}`,
          organizationId: checkout.organizationId,
          residentId: checkout.residentId,
          propertyId: checkout.propertyId,
          type: 'Deposit Refund',
          amount: checkout.refundAmount,
          reason: 'Checkout deposit refund',
          createdBy: currentUser?.id || 'system',
          createdAt: new Date().toISOString(),
          transactionReference: `REF-${Date.now().toString().slice(-8)}`,
        },
        ...prev,
      ]);
    }
    addAuditEntry({
      user: currentUser,
      role,
      action: 'Checkout Completed',
      entityType: 'Checkout',
      entityId: checkoutId,
      previousValue: checkout,
      newValue: completed,
      propertyId: checkout.propertyId,
    });
  };

  const addLead = (lead: Omit<Lead, 'id' | 'createdAt' | 'lastFollowUp'>) => {
    const newLead: Lead = {
      ...lead,
      id: `lead-${Date.now()}`,
      createdAt: new Date().toISOString().split('T')[0],
      lastFollowUp: new Date().toISOString().split('T')[0],
    };
    setLeads((prev) => [newLead, ...prev]);
    logAuditEvent('New Lead Captured', `Lead: ${lead.name}`, `Stage: ${lead.stage}, Room: ${lead.roomTypePreference}`);
  };

  const updateLeadStage = (leadId: string, stage: LeadStage, notes?: string) => {
    setLeads((prev) =>
      prev.map((l) =>
        l.id === leadId
          ? {
              ...l,
              stage,
              notes: notes ? `${l.notes}\n[${new Date().toISOString().split('T')[0]}]: ${notes}` : l.notes,
              lastFollowUp: new Date().toISOString().split('T')[0],
            }
          : l
      )
    );
    logAuditEvent('Lead Pipeline Updated', `Lead ID: ${leadId}`, `Advanced to ${stage}`);
  };

  const addMeterReading = (reading: Omit<ElectricityMeterReading, 'id'>) => {
    const newReading: ElectricityMeterReading = {
      ...reading,
      id: `emr-${Date.now()}`,
    };
    setMeterReadings((prev) => [newReading, ...prev]);
    logAuditEvent('Electricity Meter Logged', `Room ${reading.roomNumber}`, `${reading.unitsConsumed} units @ ₹${reading.ratePerUnit}/unit = ₹${reading.totalAmount}`);
  };

  const addDepositDeduction = (
    depositId: string,
    deduction: { category: 'Damage' | 'Unpaid Rent' | 'Electricity' | 'Other'; amount: number; reason: string }
  ) => {
    setDepositRecords((prev) =>
      prev.map((dep) => {
        if (dep.id !== depositId) return dep;
        const newDeductions = [
          ...dep.deductions,
          {
            id: `ded-${Date.now()}`,
            ...deduction,
            approvedByOwner: true,
          },
        ];
        const totalDeductions = newDeductions.reduce((sum, d) => sum + d.amount, 0);
        const finalRefundAmount = Math.max(0, dep.depositReceived - totalDeductions);
        return {
          ...dep,
          deductions: newDeductions,
          totalDeductions,
          finalRefundAmount,
          status: 'Settlement Pending' as const,
        };
      })
    );
    logAuditEvent('Deposit Deduction Added', `Deposit ID: ${depositId}`, `${deduction.category}: ₹${deduction.amount} - ${deduction.reason}`);
  };

  const settleDepositRefund = (depositId: string, transactionId: string) => {
    setDepositRecords((prev) =>
      prev.map((dep) => {
        if (dep.id !== depositId) return dep;
        return {
          ...dep,
          status: 'Refunded' as const,
          refundDate: new Date().toISOString().split('T')[0],
          refundTransactionId: transactionId,
        };
      })
    );
    logAuditEvent('Deposit Refund Settled', `Deposit ID: ${depositId}`, `Settled via Txn: ${transactionId}`);
  };

  const signAgreement = (agreementId: string, asRole: 'owner' | 'tenant') => {
    setAgreements((prev) =>
      prev.map((agr) => {
        if (agr.id !== agreementId) return agr;
        const ownerSigned = asRole === 'owner' ? true : agr.ownerSigned;
        const tenantSigned = asRole === 'tenant' ? true : agr.tenantSigned;
        const isBoth = ownerSigned && tenantSigned;
        return {
          ...agr,
          ownerSigned,
          tenantSigned,
          status: isBoth ? 'Active' : 'Verified',
          signedDate: isBoth ? new Date().toISOString().split('T')[0] : agr.signedDate,
        };
      })
    );
    logAuditEvent('Digital Agreement Signed', `Agreement ${agreementId}`, `Signed by ${asRole}`);
  };

  const addVisitorPass = (pass: Omit<VisitorPass, 'id' | 'status'>) => {
    const newPass: VisitorPass = {
      ...pass,
      id: `vis-${Date.now()}`,
      status: 'Pre-Approved',
    };
    setVisitorPasses((prev) => [newPass, ...prev]);
    logAuditEvent('Visitor Pre-Approved', `Visitor: ${pass.visitorName}`, `Visiting Room ${pass.roomNumber}`);
  };

  const updateVisitorStatus = (passId: string, status: VisitorPass['status']) => {
    const nowStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    setVisitorPasses((prev) =>
      prev.map((v) => {
        if (v.id !== passId) return v;
        return {
          ...v,
          status,
          checkInTime: status === 'Checked-In' ? nowStr : v.checkInTime,
          checkOutTime: status === 'Checked-Out' ? nowStr : v.checkOutTime,
        };
      })
    );
    logAuditEvent('Visitor Status Changed', `Pass ID: ${passId}`, `Status changed to ${status}`);
  };

  const logAuditEvent = (action: string, entity: string, details: string) => {
    const entry: AuditLogEntry = {
      id: `aud-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
      userId: currentUser?.id || 'sys-user',
      userName: currentUser?.name || (role === 'owner' ? 'Owner Rajesh' : 'System Admin'),
      userRole: currentUser?.role || role,
      action,
      entity,
      details,
      timestamp: new Date().toISOString().replace('T', ' ').substring(0, 16),
    };
    setAuditLogs((prev) => [entry, ...prev.slice(0, 99)]);
  };

  const updateSettings = (newSettings: Partial<SystemSettings>) => {
    setSettings((prev) => ({ ...prev, ...newSettings }));
    logAuditEvent('System Settings Modified', 'Global Configuration', 'Updated billing, currency, or SLA values');
  };

  const updateRolePermissions = (roleName: string, permissions: RolePermissions) => {
    setRolePermissions((prev) => ({ ...prev, [roleName]: permissions }));
    logAuditEvent('RBAC Permissions Updated', `Role: ${roleName}`, 'Adjusted module permissions');
  };

  const setRole = (newRole: UserRole) => {
    setRoleState(newRole);
  };

  const addStaffMember = (newStaff: Omit<StaffMember, 'id'>) => {
    const created: StaffMember = {
      ...newStaff,
      id: `staff-${Date.now()}`,
    };
    setStaff((prev) => [...prev, created]);
  };

  const updateStaffMember = (staffId: string, updates: Partial<StaffMember>) => {
    setStaff((prev) =>
      prev.map((s) => (s.id === staffId ? { ...s, ...updates } : s))
    );
  };

  const deleteStaffMember = (staffId: string) => {
    setStaff((prev) => prev.filter((s) => s.id !== staffId));
    if (currentStaffId === staffId) {
      const remaining = staff.filter((s) => s.id !== staffId);
      if (remaining[0]) setCurrentStaffId(remaining[0].id);
    }
  };

  const openAuthModal = (mode: 'login' | 'register' = 'login', targetRole?: UserRole) => {
    setAuthModalMode(mode);
    if (targetRole) {
      setAuthInitialRole(targetRole);
    }
    setAuthModalOpen(true);
  };

  const confirmDirectAction = (action: PendingCustomerAction) => {
    const isVisit = action.type === 'visit';
    const refId = `PGN-${isVisit ? 'VIS' : 'BKG'}-${Math.floor(10000 + Math.random() * 90000)}`;
    const today = new Date().toISOString().split('T')[0];

    const applicantName = action.applicantName || currentUser?.name || 'Customer';
    const email = action.email || currentUser?.email || 'customer@pgwalo.com';
    const phone = action.phone || currentUser?.phone || '+91 98765 43210';

    addBookingRequest({
      applicantName,
      email,
      phone,
      propertyId: action.property.id,
      propertyName: action.property.name,
      roomType: action.roomType || action.property.rooms[0]?.type || 'Double',
      preferredMoveInDate: action.preferredMoveInDate || action.visitDate || action.date || today,
      occupancyType: action.occupancyType || 'Working Professional',
      message: action.message || (isVisit ? `Physical tour on ${action.visitDate || action.date || 'Tomorrow'} (${action.visitTimeSlot || action.timeSlot || '10:00 AM - 12:00 PM'})` : 'Stay booking application'),
      type: action.type,
      visitDate: action.visitDate || action.date || today,
      visitTimeSlot: action.visitTimeSlot || action.timeSlot || '10:00 AM - 12:00 PM',
      referenceId: refId,
    });

    addBroadcast({
      title: isVisit ? `Visit Scheduled: ${action.property.name}` : `Booking Applied: ${action.property.name}`,
      message: isVisit
        ? `Your physical tour is scheduled for ${action.visitDate || action.date || 'Tomorrow'} (${action.visitTimeSlot || '10:00 AM - 12:00 PM'}). Pass Code: ${refId}. Caretaker: ${action.property.contactPhone}`
        : `Your stay application for ${action.property.name} has been received. Reference ID: ${refId}. Caretaker: ${action.property.contactPhone}`,
      category: 'Event',
      target: 'All Residents',
      sender: 'PGNest Concierge',
    });

    const confirmed = { referenceId: refId, action };
    setConfirmedAction(confirmed);
    return { referenceId: refId };
  };

  const login = (email: string, password?: string, requestedRole?: UserRole, isDemo: boolean = false) => {
    const cleanEmail = email.trim().toLowerCase();
    let user = users.find((u) => u.email.toLowerCase() === cleanEmail);

    // Demo mode: Skip password validation and create/update demo user
    if (isDemo) {
      // For demo, auto-provision or use existing demo user
      user = {
        id: `demo-user-${Date.now()}`,
        name: `Demo ${requestedRole || 'User'}`,
        email: cleanEmail,
        phone: '+91 98765 43210',
        role: requestedRole || 'resident',
        avatar: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=200&q=80',
        createdAt: new Date().toISOString().split('T')[0],
        isDemo: true,
        isProfileCompleted: true,
      };
      
      // Remove existing demo user with same email if exists
      setUsers((prev) => [...prev.filter(u => !u.isDemo), user!]);
      
      // Load demo data based on role
      if (requestedRole === 'owner') {
        setProperties([...DEMO_PROPERTIES]);
        setResidents([...DEMO_RESIDENTS]);
        setBookingRequests([...DEMO_BOOKING_REQUESTS]);
      } else if (requestedRole === 'resident') {
        setProperties([...DEMO_PROPERTIES]);
        setResidents([...DEMO_RESIDENTS]);
      }
      
      setCurrentUser(user);
      setRoleState(user.role);
      setAuthModalOpen(false);
      
      return {
        success: true,
        message: `Demo login successful as ${user.role}`,
        user,
      };
    }

    // For genuine user login, require existing account
    if (!user) {
      return {
        success: false,
        message: 'No account found with this email. Please check your credentials or click "Create Account".',
      };
    }

    // Validate role matches requested role if specified
    if (requestedRole && user.role !== requestedRole) {
      return {
        success: false,
        message: `This account is registered as a ${user.role}. Please use the correct login option.`,
      };
    }

    // Password validation for genuine users (skip for demo mode and Google OAuth)
    if (password && password !== 'google_oauth_session' && !isDemo && !user.isDemo) {
      // For genuine users, require correct password
      if (password !== 'demo123') {
        return {
          success: false,
          message: 'Invalid password. Please try again.',
        };
      }
    }

    // Clear demo data when a genuine user logs in
    if (!isDemo && !user.isDemo) {
      // Check if any demo data is loaded
      const hasDemoData = properties.some(p => p.id.startsWith('demo-'));
      if (hasDemoData) {
        // Clear demo data and restore genuine data
        setProperties([...INITIAL_PROPERTIES]);
        setResidents([...INITIAL_RESIDENTS]);
        setBookingRequests([...INITIAL_BOOKING_REQUESTS]);
      }
    }

    setCurrentUser(user);
    setRoleState(user.role);
    setAuthModalOpen(false);

    // If profile is not yet completed (e.g. newly registered or Google OAuth without full profile form)
    if (!user.isProfileCompleted) {
      setProfileModalOpen(true);
    }

    if (user.role === 'staff') {
      const matchedStaff = staff.find((s) => s.name.toLowerCase().includes(user!.name.toLowerCase().split(' ')[0]));
      if (matchedStaff) {
        setCurrentStaffId(matchedStaff.id);
      }
    }

    // If there was a pending customer action (schedule visit or book), execute it seamlessly
    if (pendingAction) {
      confirmDirectAction({
        ...pendingAction,
        applicantName: user.name,
        email: user.email,
        phone: user.phone,
      });
      setPendingAction(null);
    }

    return { success: true, message: `Welcome back, ${user.name}!` };
  };

  const register = (accountData: {
    name: string;
    email: string;
    phone: string;
    role: UserRole;
    password?: string;
    roomNumber?: string;
    staffRole?: string;
  }) => {
    const today = new Date().toISOString().split('T')[0];
    const newUser: UserAccount = {
      id: `user-${Date.now()}`,
      name: accountData.name,
      email: accountData.email,
      phone: accountData.phone,
      role: accountData.role,
      avatar: accountData.role === 'owner'
        ? 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=200&q=80'
        : accountData.role === 'resident'
        ? 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=200&q=80'
        : 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=200&q=80',
      // Remove hardcoded property associations - owners will create their own properties
      // residents will be associated when they book/visit a property
      propertyId: undefined,
      propertyName: undefined,
      roomNumber: undefined,
      staffRole: accountData.staffRole || (accountData.role === 'staff' ? 'Manager' : undefined),
      createdAt: today,
      isProfileCompleted: false,
    };

    setUsers((prev) => [...prev, newUser]);
    setCurrentUser(newUser);
    setRoleState(newUser.role);

    // Clear demo data when a genuine user registers
    const hasDemoData = properties.some(p => p.id.startsWith('demo-'));
    if (hasDemoData) {
      setProperties([...INITIAL_PROPERTIES]);
      setResidents([...INITIAL_RESIDENTS]);
      setBookingRequests([...INITIAL_BOOKING_REQUESTS]);
    }

    // Only create resident data if they have an actual property assignment (from booking/visit)
    // For new registrations, residents start without property association
    if (accountData.role === 'staff') {
      const newStaffMember: StaffMember = {
        id: `staff-${Date.now()}`,
        name: accountData.name,
        role: (accountData.staffRole as any) || 'Manager',
        phone: accountData.phone,
        avatar: newUser.avatar,
        propertyId: undefined, // Staff will be assigned to properties by owners
        shift: 'Morning (6 AM - 2 PM)',
        todayStatus: 'Available',
        lastClockIn: undefined,
      };
      setStaff((prev) => [...prev, newStaffMember]);
      setCurrentStaffId(newStaffMember.id);
    }

    setAuthModalOpen(false);
    // Bring up the profile form immediately so they can review and add age, emergency contacts, etc.
    setProfileModalOpen(true);

    // If there was a pending customer action, execute it seamlessly
    if (pendingAction) {
      confirmDirectAction({
        ...pendingAction,
        applicantName: newUser.name,
        email: newUser.email,
        phone: newUser.phone,
      });
      setPendingAction(null);
    }

    return { success: true, message: 'Account created successfully!' };
  };

  const updateUserProfile = (updates: Partial<UserAccount>) => {
    if (!currentUser) return { success: false, message: 'No active session.' };

    const updatedUser: UserAccount = {
      ...currentUser,
      ...updates,
      isProfileCompleted: true,
    };

    setCurrentUser(updatedUser);
    setUsers((prev) => {
      const exists = prev.some((u) => u.id === updatedUser.id);
      if (exists) {
        return prev.map((u) => (u.id === updatedUser.id ? updatedUser : u));
      }
      return [...prev, updatedUser];
    });

    if (updates.role) {
      setRoleState(updates.role);
    }

    if (updatedUser.role === 'resident') {
      setResidents((prev) => {
        const existingIdx = prev.findIndex(
          (r) =>
            (updatedUser.email && r.email.toLowerCase() === updatedUser.email.toLowerCase()) ||
            r.id === `res-${updatedUser.id}` ||
            r.id === updatedUser.id
        );

        if (existingIdx >= 0) {
          const next = [...prev];
          next[existingIdx] = {
            ...next[existingIdx],
            name: updatedUser.name,
            email: updatedUser.email,
            phone: updatedUser.phone,
            avatar: updatedUser.avatar || next[existingIdx].avatar,
            emergencyContact: updatedUser.emergencyContactPhone || next[existingIdx].emergencyContact,
          };
          return next;
        }
        // Do not inject fake room allocation for newly registered users
        return prev;
      });
    }

    return { success: true, message: 'Profile saved successfully!' };
  };

  const logout = () => {
    // Clear demo data if current user is a demo user
    if (currentUser?.isDemo) {
      setUsers((prev) => prev.filter(u => !u.isDemo));
      setProperties((prev) => prev.filter(p => !p.id.startsWith('demo-')));
      setResidents((prev) => prev.filter(r => !r.id.startsWith('demo-')));
      setBookingRequests((prev) => prev.filter(b => !b.id.startsWith('demo-')));
    }
    
    setCurrentUser(null);
    setRoleState('public');
    localStorage.removeItem(STORAGE_KEYS.CURRENT_USER);
    localStorage.removeItem(STORAGE_KEYS.ROLE);
    clearAuthToken(); // Clear JWT token for Cloudflare Workers
  };

  const addProperty = (newProp: Omit<Property, 'id'>) => {
    const created: Property = {
      ...newProp,
      id: `prop-${Date.now()}`,
    };
    setProperties((prev) => [created, ...prev]);
  };

  const addBookingRequest = (request: Omit<BookingRequest, 'id' | 'status' | 'requestDate'>) => {
    const today = new Date().toISOString().split('T')[0];
    const newReq: BookingRequest = {
      ...request,
      id: `req-${Date.now()}`,
      status: 'Pending',
      requestDate: today,
    };
    setBookingRequests((prev) => [newReq, ...prev]);
  };

  const approveBookingRequest = (requestId: string, roomNumber?: string, bedNumber?: string) => {
    const assignedRoom = roomNumber || '204';
    const assignedBed = bedNumber || 'Bed A';

    let targetReq = bookingRequests.find((r) => r.id === requestId);
    const requestedBed =
      beds.find(
        (bed) =>
          bed.roomNumber === assignedRoom &&
          (bed.bedNumber === assignedBed || bed.bedNumber.endsWith(assignedBed.replace(/^Bed\s*/i, '')))
      ) ||
      beds.find((bed) => bed.roomNumber === assignedRoom && canBedBeAssigned(bed)) ||
      beds.find((bed) => bed.sharingType === targetReq?.roomType && canBedBeAssigned(bed));

    if (requestedBed && !canBedBeAssigned(requestedBed)) {
      logAuditEvent('Blocked Double Allocation', `Bed ${requestedBed.id}`, 'Booking approval attempted on an occupied bed');
      return;
    }

    setBookingRequests((prev) =>
      prev.map((req) => {
        if (req.id === requestId) {
          targetReq = req;
          return {
            ...req,
            status: 'Approved',
            allocatedRoomNumber: requestedBed?.roomNumber || assignedRoom,
            allocatedBedNumber: requestedBed?.bedNumber || assignedBed,
            residentStatus: 'Pending Move-In',
            reservedBedId: requestedBed?.id,
            reservationExpiry: req.reservationExpiry || req.preferredMoveInDate,
          };
        }
        return req;
      })
    );

    if (targetReq) {
      const existingRes = residents.find(
        (r) =>
          (targetReq!.email && r.email.toLowerCase() === targetReq!.email.toLowerCase()) ||
          r.phone === targetReq!.phone
      );

      const newRes: Resident = {
        id: existingRes?.id || `res-${Date.now()}`,
        name: targetReq.applicantName,
        email: targetReq.email,
        phone: targetReq.phone,
        avatar: existingRes?.avatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=200&q=80',
        organizationId: targetReq.organizationId || DEFAULT_ORGANIZATION_ID,
        status: 'Pending Move-In',
        propertyId: targetReq.propertyId,
        propertyName: targetReq.propertyName,
        roomNumber: requestedBed?.roomNumber || assignedRoom,
        roomType: targetReq.roomType || 'Double',
        bedNumber: requestedBed?.bedNumber || assignedBed,
        monthlyRent: requestedBed?.monthlyRent || (targetReq.roomType === 'Single' ? 14000 : targetReq.roomType === 'Double' ? 9500 : 7800),
        depositAmount: requestedBed?.deposit || 15000,
        moveInDate: targetReq.preferredMoveInDate || new Date().toISOString().split('T')[0],
        rentStatus: 'Pending',
        rentDueDate: '07th Every Month',
        emergencyContact: targetReq.phone,
        kycVerified: true,
        depositState: 'Pending',
        agreementState: 'Pending',
        previousDues: 0,
        advanceBalance: 0,
        outstandingBalance: requestedBed?.monthlyRent || (targetReq.roomType === 'Single' ? 14000 : targetReq.roomType === 'Double' ? 9500 : 7800),
        notes: targetReq.message || 'Approved from online application',
      };

      // Also mark the allocated Bed as Occupied
      setBeds((prev) =>
        prev.map((b) =>
          b.roomNumber === assignedRoom && b.bedNumber === assignedBed
            ? { ...b, status: 'Occupied', currentTenantId: newRes.id, currentTenantName: newRes.name }
            : b
        )
      );

      // Record Stay
      const todayStr = new Date().toISOString().split('T')[0];
      const newStay: Stay = {
        id: `stay-${Date.now()}`,
        organizationId: newRes.organizationId || DEFAULT_ORGANIZATION_ID,
        residentId: newRes.id,
        propertyId: newRes.propertyId,
        roomId: beds.find(b => b.bedNumber === assignedBed)?.roomId,
        startDate: newRes.moveInDate || todayStr,
        status: 'Current',
        endDate: undefined,
        createdAt: todayStr,
      };
      setStays((prev) => [newStay, ...prev]);

      setResidents((prev) => {
        const filtered = prev.filter(
          (r) => r.email.toLowerCase() !== targetReq!.email.toLowerCase()
        );
        return [newRes, ...filtered];
      });

      if (requestedBed) {
        setBeds((prev) =>
          prev.map((bed) =>
            bed.id === requestedBed.id
              ? {
                  ...bed,
                  status: 'Reserved',
                  currentTenantId: newRes.id,
                  currentTenantName: newRes.name,
                  reservedForResidentId: newRes.id,
                  reservationExpiry: targetReq?.preferredMoveInDate,
                }
              : bed
          )
        );
        setStays((prev) => [
          {
            id: `stay-${newRes.id}-reserved`,
            organizationId: newRes.organizationId || DEFAULT_ORGANIZATION_ID,
            residentId: newRes.id,
            propertyId: newRes.propertyId,
            roomId: requestedBed.roomId,
            roomNumber: requestedBed.roomNumber,
            bedId: requestedBed.id,
            bedNumber: requestedBed.bedNumber,
            startDate: newRes.moveInDate,
            monthlyRentAtStart: newRes.monthlyRent,
            status: 'Reserved',
          },
          ...prev.filter((stay) => stay.residentId !== newRes.id || stay.status !== 'Reserved'),
        ]);
        setRentPlans((prev) => [
          {
            id: `rent-plan-${newRes.id}`,
            organizationId: newRes.organizationId || DEFAULT_ORGANIZATION_ID,
            residentId: newRes.id,
            propertyId: newRes.propertyId,
            monthlyRent: newRes.monthlyRent,
            dueDay: settings.rentDueDay || 7,
            effectiveFrom: newRes.moveInDate,
            status: 'Active',
          },
          ...prev.filter((plan) => plan.residentId !== newRes.id),
        ]);
      }

      if (currentUser && currentUser.email && currentUser.email.toLowerCase() === targetReq.email.toLowerCase()) {
        const updatedUser: UserAccount = {
          ...currentUser,
          propertyId: targetReq.propertyId,
          propertyName: targetReq.propertyName,
          roomNumber: requestedBed?.roomNumber || assignedRoom,
        };
        setCurrentUser(updatedUser);
      }

      addBroadcast({
        title: `Room Allocation Approved: Room ${assignedRoom}`,
        message: `Welcome ${targetReq.applicantName}! Your booking for ${targetReq.propertyName} has been approved. Room ${assignedRoom} (${assignedBed}) is now allocated.`,
        category: 'Event',
        target: 'All Residents',
        sender: 'Property Owner (Rajesh Sharma)',
      });

      addAuditEntry({
        user: currentUser,
        role,
        action: 'Reservation Approved',
        entityType: 'BookingRequest',
        entityId: requestId,
        previousValue: targetReq,
        newValue: { residentId: newRes.id, bedId: requestedBed?.id, status: 'Pending Move-In' },
        propertyId: targetReq.propertyId,
      });
    }
  };

  const rejectBookingRequest = (requestId: string) => {
    setBookingRequests((prev) =>
      prev.map((req) => (req.id === requestId ? { ...req, status: 'Rejected' } : req))
    );
  };

  const cancelBookingRequest = (requestId: string) => {
    setBookingRequests((prev) =>
      prev.map((req) => (req.id === requestId ? { ...req, status: 'Cancelled' } : req))
    );
  };

  const rescheduleVisit = (requestId: string, newDate: string, newTimeSlot: string) => {
    setBookingRequests((prev) =>
      prev.map((req) =>
        req.id === requestId
          ? {
              ...req,
              visitDate: newDate,
              preferredMoveInDate: newDate,
              visitTimeSlot: newTimeSlot,
              status: 'Pending',
            }
          : req
      )
    );
    addBroadcast({
      title: 'Visit Rescheduled',
      message: `Your physical tour has been rescheduled to ${newDate} (${newTimeSlot}).`,
      category: 'Event',
      target: 'All Residents',
      sender: 'PGNest Concierge',
    });
  };

  const addResident = (res: Omit<Resident, 'id'>) => {
    const newRes: Resident = {
      ...res,
      id: `res-${Date.now()}`,
    };
    setResidents((prev) => [newRes, ...prev]);
  };

  const updateRentStatus = (residentId: string, status: 'Paid' | 'Pending' | 'Overdue') => {
    if (status === 'Paid') {
      logAuditEvent('Blocked Silent Financial Edit', `Resident ${residentId}`, 'Use payment recording and verification instead of direct paid toggles');
      return;
    }
    setResidents((prev) =>
      prev.map((r) =>
        r.id === residentId
          ? {
              ...r,
              rentStatus: status,
              lastPaymentDate: r.lastPaymentDate,
            }
          : r
      )
    );
  };

  const updateResidentStatus = (residentId: string, newStatus: ResidentStatus) => {
    setResidents((prev) =>
      prev.map((r) => (r.id === residentId ? { ...r, status: newStatus } : r))
    );
    logAuditEvent('Resident Status Changed', `Resident ${residentId}`, `Updated lifecycle status to ${newStatus}`);
  };

  const updatePropertyModules = (propertyId: string, modules: Partial<Property>) => {
    setProperties((prev) =>
      prev.map((p) => (p.id === propertyId ? { ...p, ...modules } : p))
    );
    logAuditEvent('Property Config Updated', `Property ${propertyId}`, `Updated property operational modules.`);
  };

  const generateMonthlyInvoices = (monthYear: string = 'September 2026'): number => {
    let generatedCount = 0;
    const todayStr = new Date().toISOString().split('T')[0];

    setInvoices((prev) => {
      const updated = [...prev];
      residents.forEach((res) => {
        // Skip if invoice already exists for this resident and month
        const exists = updated.some((inv) => inv.residentId === res.id && inv.month === monthYear);
        if (exists) return;

        // Calculate electricity charges from meter readings if available
        const roomReadings = meterReadings.filter((m) => m.roomNumber === res.roomNumber);
        const latestReading = roomReadings[0];
        const electricityCharges = latestReading ? Math.round(latestReading.calculatedAmount) : 480;

        const baseRent = res.monthlyRent;
        const lineItems = [
          {
            id: `li-${Date.now()}-1`,
            description: `${monthYear} Room Rent (${res.roomType})`,
            amount: baseRent,
            type: 'Rent' as const,
          },
          {
            id: `li-${Date.now()}-2`,
            description: 'Room Electricity Sub-meter',
            amount: electricityCharges,
            type: 'Electricity' as const,
          },
        ];

        const previousDues = res.previousDues || 0;
        const advanceBalance = res.advanceBalance || 0;
        const advanceDeducted = Math.min(advanceBalance, baseRent + electricityCharges + previousDues);
        const subtotal = baseRent + electricityCharges;
        const totalDue = Math.max(0, subtotal + previousDues - advanceDeducted);

        const newInvoice: Invoice = {
          id: `inv-${Date.now()}-${res.id.slice(-4)}`,
          invoiceNumber: `INV-${new Date().getFullYear()}-${String(updated.length + 1).padStart(4, '0')}`,
          residentId: res.id,
          residentName: res.name,
          propertyId: res.propertyId,
          propertyName: res.propertyName,
          roomNumber: res.roomNumber,
          month: monthYear,
          billingCycleStart: `${todayStr.slice(0, 7)}-01`,
          billingCycleEnd: `${todayStr.slice(0, 7)}-30`,
          dueDate: res.rentDueDate || `${todayStr.slice(0, 7)}-07`,
          baseRent,
          electricityCharges,
          otherCharges: 0,
          lineItems,
          subtotal,
          previousDuesApplied: previousDues,
          advanceDeducted,
          totalDue,
          amountPaid: advanceDeducted > 0 && totalDue === 0 ? subtotal + previousDues : 0,
          outstandingBalance: totalDue,
          status: totalDue === 0 ? 'Paid' : 'Due',
          createdAt: todayStr,
          updatedAt: todayStr,
          receiptIds: [],
        };

        updated.unshift(newInvoice);
        generatedCount++;
      });
      return updated;
    });

    logAuditEvent('Invoices Generated', `Cycle: ${monthYear}`, `Generated ${generatedCount} itemized invoices across all active residents.`);
    return generatedCount;
  };

  const recordPaymentForInvoice = (
    invoiceId: string,
    amount: number,
    paymentMethod: string,
    notes?: string
  ): PaymentReceipt => {
    const today = new Date().toISOString().split('T')[0];
    const txnId = `PGN-${Date.now().toString().slice(-8)}`;

    let generatedReceipt: PaymentReceipt | null = null;

    setInvoices((prev) =>
      prev.map((inv) => {
        if (inv.id !== invoiceId) return inv;

        const newAmountPaid = inv.amountPaid + amount;
        const newBalance = Math.max(0, inv.totalDue - newAmountPaid);
        const newStatus = newBalance === 0 ? 'Paid' : 'Partially Paid';

        generatedReceipt = {
          transactionId: txnId,
          residentName: inv.residentName,
          propertyName: inv.propertyName,
          roomNumber: inv.roomNumber,
          amount,
          month: inv.month,
          paymentMethod,
          paidAt: new Date().toLocaleString(),
          status: 'Success',
          rentAmount: inv.baseRent,
          electricityAmount: inv.electricityCharges,
        };

        return {
          ...inv,
          amountPaid: newAmountPaid,
          outstandingBalance: newBalance,
          status: newStatus,
          updatedAt: today,
          receiptIds: [...inv.receiptIds, txnId],
        };
      })
    );

    // Update resident state
    const targetInvoice = invoices.find((i) => i.id === invoiceId);
    if (targetInvoice) {
      setResidents((prev) =>
        prev.map((r) => {
          if (r.id !== targetInvoice.residentId) return r;
          const isFullyPaid = targetInvoice.totalDue <= (targetInvoice.amountPaid + amount);
          return {
            ...r,
            rentStatus: isFullyPaid ? 'Paid' : 'Pending',
            lastPaymentDate: today,
          };
        })
      );

      logAuditEvent(
        'Invoice Payment Recorded',
        `Invoice ${targetInvoice.invoiceNumber}`,
        `Payment of ₹${amount.toLocaleString()} via ${paymentMethod}. Outstanding: ₹${Math.max(0, targetInvoice.totalDue - targetInvoice.amountPaid - amount)}`
      );
    }

    return generatedReceipt || {
      transactionId: txnId,
      residentName: targetInvoice?.residentName || 'Resident',
      propertyName: targetInvoice?.propertyName || 'PGNest',
      roomNumber: targetInvoice?.roomNumber || 'Room',
      amount,
      month: targetInvoice?.month || 'Current Month',
      paymentMethod,
      paidAt: new Date().toLocaleString(),
      status: 'Success',
    };
  };

  const recordAdvancePayment = (residentId: string, amount: number, paymentMethod: string) => {
    setResidents((prev) =>
      prev.map((r) => {
        if (r.id !== residentId) return r;
        const newAdvance = (r.advanceBalance || 0) + amount;
        return {
          ...r,
          advanceBalance: newAdvance,
        };
      })
    );
    logAuditEvent('Advance Payment Credited', `Resident ${residentId}`, `Credited ₹${amount.toLocaleString()} advance balance via ${paymentMethod}.`);
  };

  const processRoomTransfer = (
    residentId: string,
    newRoomNumber: string,
    newBedNumber: string,
    newMonthlyRent: number,
    reason: string
  ) => {
    const today = new Date().toISOString().split('T')[0];
    const targetResident = residents.find((r) => r.id === residentId);
    if (!targetResident) return;

    const oldRoom = targetResident.roomNumber;
    const oldBed = targetResident.bedNumber;

    // 1. Terminate old stay record and add new stay record
    setStays((prev) => {
      const updated = prev.map((s) =>
        s.residentId === residentId && s.status === 'Active'
          ? { ...s, status: 'Transferred' as const, endDate: today, transferReason: reason }
          : s
      );
      const newStay: StayRecord = {
        id: `stay-${Date.now()}`,
        residentId,
        residentName: targetResident.name,
        propertyId: targetResident.propertyId,
        propertyName: targetResident.propertyName,
        roomNumber: newRoomNumber,
        bedNumber: newBedNumber,
        roomType: targetResident.roomType,
        startDate: today,
        monthlyRent: newMonthlyRent,
        status: 'Active',
        createdAt: today,
      };
      return [newStay, ...updated];
    });

    // 2. Free up old bed and occupy new bed
    setBeds((prev) =>
      prev.map((b) => {
        if (b.roomNumber === oldRoom && b.bedNumber === oldBed) {
          return { ...b, status: 'Cleaning', currentTenantId: undefined, currentTenantName: undefined };
        }
        if (b.roomNumber === newRoomNumber && b.bedNumber === newBedNumber) {
          return { ...b, status: 'Occupied', currentTenantId: residentId, currentTenantName: targetResident.name };
        }
        return b;
      })
    );

    // 3. Update resident record
    setResidents((prev) =>
      prev.map((r) =>
        r.id === residentId
          ? {
              ...r,
              roomNumber: newRoomNumber,
              bedNumber: newBedNumber,
              monthlyRent: newMonthlyRent,
            }
          : r
      )
    );

    logAuditEvent(
      'Resident Room Transferred',
      `Resident: ${targetResident.name}`,
      `Transferred from Room ${oldRoom} (${oldBed}) to Room ${newRoomNumber} (${newBedNumber}). Reason: ${reason}`
    );
  };

  const initiateNoticePeriod = (
    residentId: string,
    noticeDate: string = new Date().toISOString().split('T')[0],
    checkoutDate?: string,
    reason?: string
  ) => {
    const d = new Date(noticeDate);
    d.setDate(d.getDate() + 30);
    const expectedCheckout = checkoutDate || d.toISOString().split('T')[0];

    setResidents((prev) =>
      prev.map((r) => {
        if (r.id !== residentId) return r;
        return {
          ...r,
          status: 'Notice Period',
          noticeDate,
          expectedCheckoutDate: expectedCheckout,
          checkoutReason: reason || 'Notice period submitted by resident',
        };
      })
    );

    // Set Bed to 'Notice Period'
    const res = residents.find((r) => r.id === residentId);
    if (res) {
      setBeds((prev) =>
        prev.map((b) =>
          b.roomNumber === res.roomNumber && b.bedNumber === res.bedNumber
            ? { ...b, status: 'Notice Period' }
            : b
        )
      );
    }

    logAuditEvent(
      'Notice Period Initiated',
      `Resident ${res?.name || residentId}`,
      `Notice given on ${noticeDate}. Expected move-out: ${expectedCheckout}. Reason: ${reason || 'Standard 30-day notice'}`
    );
  };

  const executeCheckoutSettlement = (
    settlementData: Omit<CheckoutSettlement, 'id'>
  ): CheckoutSettlement => {
    const today = new Date().toISOString().split('T')[0];
    const newSettlement: CheckoutSettlement = {
      ...settlementData,
      id: `chk-${Date.now()}`,
      settledAt: today,
    };

    setCheckoutSettlements((prev) => [newSettlement, ...prev]);

    // Log the settlement
    logAuditEvent(
      'Checkout Settlement Executed',
      `Resident ${settlementData.residentName}`,
      `Checkout on ${settlementData.checkoutDate}. Deposit: ${settlementData.depositHeld}, Refund: ${settlementData.finalRefundAmount}`
    );

    // 1. Mark resident as Checked Out
    setResidents((prev) =>
      prev.map((r) =>
        r.id === settlementData.residentId
          ? {
              ...r,
              status: 'Checked Out',
              actualCheckoutDate: settlementData.checkoutDate,
              rentStatus: 'Paid',
            }
          : r
      )
    );

    // 2. Transition Bed to 'Cleaning'
    setBeds((prev) =>
      prev.map((b) =>
        b.roomNumber === settlementData.roomNumber && b.bedNumber === settlementData.bedNumber
          ? {
              ...b,
              status: 'Cleaning',
              currentTenantId: undefined,
              currentTenantName: undefined,
            }
          : b
      )
    );

    // 3. Mark Stay as Completed
    setStays((prev) =>
      prev.map((s) =>
        s.residentId === settlementData.residentId && s.status === 'Active'
          ? { ...s, status: 'Completed', endDate: settlementData.checkoutDate }
          : s
      )
    );

    // 4. Update SecurityDepositRecord
    setDepositRecords((prev) =>
      prev.map((d) =>
        d.residentId === settlementData.residentId
          ? {
              ...d,
              status: 'Refunded',
              refundDate: today,
              refundTransactionId: settlementData.refundTransactionId || `UPI-REF-${Date.now().toString().slice(-6)}`,
              finalRefundAmount: settlementData.finalRefundAmount,
            }
          : d
      )
    );

    logAuditEvent(
      'Checkout Settlement Executed',
      `Resident: ${settlementData.residentName}`,
      `Security deposit ₹${settlementData.depositHeld} settled with deductions ₹${settlementData.totalDeductions}. Refund amount: ₹${settlementData.finalRefundAmount}`
    );

    return newSettlement;
  };

  const payRentSimulation = (residentId: string, method: string): PaymentReceipt => {
    const resident = residents.find((r) => r.id === residentId) || currentResident;
    if (!resident) {
      throw new Error('Resident not found for payment.');
    }
    const invoice =
      invoices.find((item) => item.residentId === residentId && item.status !== 'Paid' && item.status !== 'Cancelled') ||
      generateMonthlyInvoice(residentId);
    const amount = invoice ? Math.max(0, invoice.amount - invoice.verifiedPaidAmount) : resident.monthlyRent;
    const payment = recordPayment(residentId, amount, method, 'Verified');
    const verifiedReceipt: PaymentReceipt = {
      transactionId: payment.transactionReference,
      residentName: resident.name,
      propertyName: resident.propertyName,
      roomNumber: resident.roomNumber,
      amount,
      month: invoice?.month || new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' }),
      paymentMethod: method,
      paidAt: new Date().toLocaleString(),
      status: 'Success',
    };
    setBroadcasts((prev) => [
      {
        id: `b-${Date.now()}`,
        title: `Rent Payment Received - Room ${resident.roomNumber}`,
        message: `Verified rent payment of Rs ${amount.toLocaleString()} by ${resident.name} for ${verifiedReceipt.month} via ${method}. Transaction ID: ${verifiedReceipt.transactionId}`,
        category: 'Rent',
        target: 'All Residents',
        timestamp: new Date().toLocaleString(),
        sender: 'PGNest Payment Gateway',
      },
      ...prev,
    ]);
    return verifiedReceipt;
    const today = new Date().toISOString().split('T')[0];
    
    // Check if an invoice exists for this resident
    const openInvoice = invoices.find(
      (inv) => inv.residentId === residentId && inv.status !== 'Paid'
    );

    if (openInvoice) {
      return recordPaymentForInvoice(openInvoice.id, openInvoice.outstandingBalance, method, 'Self-service rent payment');
    }

    const receipt: PaymentReceipt = {
      transactionId: `PGN-${Date.now().toString().slice(-8)}`,
      residentName: resident?.name || currentUser?.name || 'Resident',
      propertyName: resident?.propertyName || 'PGNest Property',
      roomNumber: resident?.roomNumber || '204',
      amount: resident?.monthlyRent || 8500,
      month: 'September 2026',
      paymentMethod: method,
      paidAt: new Date().toLocaleString(),
      status: 'Success',
    };

    updateRentStatus(residentId, 'Paid');

    // Add confirmation broadcast notification
    const newBroadcast: BroadcastNotification = {
      id: `b-${Date.now()}`,
      title: `Rent Payment Received — Room ${resident?.roomNumber || '204'}`,
      message: `Rent payment of ₹${(resident?.monthlyRent || 8500).toLocaleString()} by ${resident?.name || 'Resident'} was confirmed via ${method}. Transaction ID: ${receipt.transactionId}`,
      category: 'Rent',
      target: 'All Residents',
      timestamp: new Date().toLocaleString(),
      sender: 'PGNest Payment Gateway',
    };
    setBroadcasts((prev) => [newBroadcast, ...prev]);

    return receipt;
  };

  const recordAttendance = (record: Omit<AttendanceRecord, 'id' | 'date' | 'timestamp'>) => {
    const now = new Date();
    const timeString = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const dateString = now.toISOString().split('T')[0];
    const newRec: AttendanceRecord = {
      ...record,
      id: `att-${Date.now()}`,
      date: dateString,
      timestamp: timeString,
    };
    setAttendance((prev) => [newRec, ...prev]);
  };

  const toggleStaffClockIn = (staffId: string) => {
    const now = new Date();
    const timeString = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const dateString = now.toISOString().split('T')[0];

    setStaff((prev) =>
      prev.map((s) => {
        if (s.id === staffId) {
          const nextStatus = s.todayStatus === 'Checked-In' ? 'Checked-Out' : 'Checked-In';
          // record attendance
          const newRec: AttendanceRecord = {
            id: `att-${Date.now()}`,
            personId: s.id,
            personName: s.name,
            personType: 'Staff',
            role: s.role,
            date: dateString,
            timestamp: timeString,
            type: nextStatus === 'Checked-In' ? 'Check-In' : 'Check-Out',
            status: 'On-Time',
            notes: `${s.role} logged duty ${nextStatus}`,
          };
          setAttendance((a) => [newRec, ...a]);
          return {
            ...s,
            todayStatus: nextStatus,
            lastClockIn: nextStatus === 'Checked-In' ? timeString : s.lastClockIn,
          };
        }
        return s;
      })
    );
  };

  const toggleTaskCompleted = (taskId: string) => {
    setTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, completed: !t.completed } : t))
    );
  };

  const addTask = (task: Omit<StaffTask, 'id' | 'completed'>) => {
    const newTask: StaffTask = {
      ...task,
      id: `task-${Date.now()}`,
      completed: false,
    };
    setTasks((prev) => [newTask, ...prev]);
  };

  const addBroadcast = (broadcast: Omit<BroadcastNotification, 'id' | 'timestamp'>) => {
    const newB: BroadcastNotification = {
      ...broadcast,
      id: `b-${Date.now()}`,
      timestamp: new Date().toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }),
    };
    setBroadcasts((prev) => [newB, ...prev]);
  };

  const updateMealPlanDay = (day: MealPlanDay['day'], field: keyof MealPlanDay, value: string) => {
    setMealPlan((prev) =>
      prev.map((m) => (m.day === day ? { ...m, [field]: value } : m))
    );
  };

  const sendChatMessage = (text: string, isOwner: boolean) => {
    const residentName = currentResident?.name || currentUser?.name || 'Resident';
    const senderName = isOwner ? 'Rajesh Sharma (Owner)' : residentName;
    const recipientName = isOwner ? residentName : 'Rajesh Sharma (Owner)';
    const msg: ChatMessage = {
      id: `msg-${Date.now()}`,
      senderRole: isOwner ? 'owner' : 'resident',
      senderName,
      recipientName,
      text,
      timestamp: 'Just now',
      isOwner,
    };
    setChatMessages((prev) => [...prev, msg]);
  };

  const addMaintenanceTicket = (ticket: Omit<MaintenanceTicket, 'id' | 'createdAt' | 'status'>) => {
    const newTkt: MaintenanceTicket = {
      ...ticket,
      id: `tkt-${Date.now()}`,
      status: 'Reported',
      createdAt: new Date().toISOString().split('T')[0],
    };
    setTickets((prev) => [newTkt, ...prev]);
  };

  const updateTicketStatus = (ticketId: string, status: MaintenanceTicket['status']) => {
    setTickets((prev) =>
      prev.map((t) => (t.id === ticketId ? { ...t, status } : t))
    );
  };

  const resetToDemoData = () => {
    localStorage.clear();
    setOrganizations([
      {
        id: DEFAULT_ORGANIZATION_ID,
        name: 'PGWalo Operations',
        ownerUserId: 'user-owner',
        accountState: 'Trial / Pending Setup',
        subscriptionPlan: 'Trial',
        createdAt: new Date().toISOString(),
      },
    ]);
    setProperties(INITIAL_PROPERTIES);
    setResidents(INITIAL_RESIDENTS);
    setBookingRequests(INITIAL_BOOKING_REQUESTS);
    setAttendance(INITIAL_ATTENDANCE);
    setStaff(INITIAL_STAFF);
    setTasks(INITIAL_TASKS);
    setBroadcasts(INITIAL_BROADCASTS);
    setMealPlan(INITIAL_MEAL_PLAN);
    setChatMessages(INITIAL_CHAT);
    setTickets(INITIAL_TICKETS);
    setUsers(INITIAL_USERS);
    setBeds(INITIAL_BEDS);
    setLeads(INITIAL_LEADS);
    setMeterReadings(INITIAL_METER_READINGS);
    setReconciliations(INITIAL_RECONCILIATION);
    setDepositRecords(INITIAL_DEPOSITS);
    setAgreements(INITIAL_AGREEMENTS);
    setVisitorPasses(INITIAL_VISITORS);
    setAuditLogs(INITIAL_AUDIT_LOGS);
    setSettings(INITIAL_SETTINGS);
    setRolePermissions(DEFAULT_ROLE_PERMISSIONS);
    setStays(deriveInitialStays(INITIAL_RESIDENTS, INITIAL_BEDS));
    setRentPlans(deriveInitialRentPlans(INITIAL_RESIDENTS, INITIAL_PROPERTIES));
    setInvoices([]);
    setPayments([]);
    setPaymentAllocations([]);
    setDepositTransactions(
      INITIAL_RESIDENTS.map((resident) => ({
        id: `dep-txn-${resident.id}`,
        organizationId: resident.organizationId || DEFAULT_ORGANIZATION_ID,
        residentId: resident.id,
        propertyId: resident.propertyId,
        type: 'Deposit Received' as const,
        amount: resident.depositAmount,
        reason: 'Opening security deposit balance',
        createdBy: 'system',
        createdAt: resident.moveInDate,
      }))
    );
    setNotices([]);
    setCheckouts([]);
    setCurrentUser(null);
    setCurrentStaffId('staff-1');
    setRoleState('public');
  };

  return (
    <AppContext.Provider
      value={{
        role,
        setRole,
        setRoleState,
        currentUser,
        organizations,
        users,
        properties,
        residents,
        bookingRequests,
        attendance,
        staff,
        currentStaff,
        setCurrentStaffId,
        tasks,
        broadcasts,
        mealPlan,
        chatMessages,
        tickets,
        currentResident,
        activeProperty,
        selectedPGForDetail,
        setSelectedPGForDetail,
        showAuthModal: authModalOpen,
        setShowAuthModal: setAuthModalOpen,
        authModalOpen,
        setAuthModalOpen,
        authModalMode,
        setAuthModalMode,
        authInitialRole,
        setAuthInitialRole,
        openAuthModal,
        login,
        register,
        logout,
        profileModalOpen,
        setProfileModalOpen,
        updateUserProfile,
        pendingAction,
        setPendingAction,
        confirmedAction,
        setConfirmedAction,
        confirmDirectAction,
        beds,
        updateBedStatus,
        stays,
        rentPlans,
        invoices,
        payments,
        paymentAllocations,
        depositTransactions,
        notices,
        checkouts,
        checkoutSettlements,
        hasPermission,
        generateMonthlyInvoice,
        recordPayment,
        verifyPayment,
        getResidentLedgerSummary,
        reserveBedForResident,
        confirmMoveIn,
        transferResidentBed,
        submitNotice,
        approveNotice,
        startCheckout,
        completeCheckout,
        leads,
        addLead,
        updateLeadStage,
        meterReadings,
        addMeterReading,
        reconciliations,
        depositRecords,
        addDepositDeduction,
        settleDepositRefund,
        agreements,
        signAgreement,
        visitorPasses,
        addVisitorPass,
        updateVisitorStatus,
        auditLogs,
        logAuditEvent,
        settings,
        updateSettings,
        rolePermissions,
        updateRolePermissions,
        generateMonthlyInvoices,
        recordPaymentForInvoice,
        recordAdvancePayment,
        processRoomTransfer,
        initiateNoticePeriod,
        executeCheckoutSettlement,
        updateResidentStatus,
        updatePropertyModules,
        globalSearchQuery,
        setGlobalSearchQuery,
        showAIOnboardingModal,
        setShowAIOnboardingModal,
        showVirtualTourModal,
        setShowVirtualTourModal,
        virtualTourRoom,
        setVirtualTourRoom,
        addProperty,
        addBookingRequest,
        approveBookingRequest,
        rejectBookingRequest,
        cancelBookingRequest,
        rescheduleVisit,
        addResident,
        updateRentStatus,
        payRentSimulation,
        recordAttendance,
        toggleStaffClockIn,
        addStaffMember,
        updateStaffMember,
        deleteStaffMember,
        toggleTaskCompleted,
        addTask,
        addBroadcast,
        updateMealPlanDay,
        sendChatMessage,
        addMaintenanceTicket,
        updateTicketStatus,
        resetToDemoData,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
